import { currencyFormatter } from '@/helpers/currencyFormatter';
import { Badge, Button, Table, useOpenClose } from '@palmetto/palmetto-components';
import type { Column, Row } from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PricingPlanStatus } from 'types';

import { useGetSeasonalPricingPlanQuery } from '../../../services/pricing';
import type { SeasonalPricingPlan } from '../../../services/pricing';
import RequirePricingAdminUser from '../../auth/RequirePricingAdminUser';
import DateTimeDisplay from '../../DateTime';

import { ANNUAL_EQUIVALENT_TOTAL, MONTH_KEYS, MONTH_LABELS, seasonalPlansPath, sumMultipliers } from './constants';
import { SeasonalPlanModal, toFormValues } from './SeasonalPlanModal';

interface SeasonalPlanDetailProps {
  programType: string;
}

/** Only non-equivalent versions carry an acknowledgement, so the union has to be narrowed before reading it. */
const getAcknowledgedBy = (plan: SeasonalPricingPlan): string | undefined =>
  plan.annualEquivalent ? undefined : plan.exception.acknowledgedBy;

export function SeasonalPlanDetail({ programType }: SeasonalPlanDetailProps) {
  const { planGroupId = '' } = useParams<{ planGroupId: string }>();
  const navigate = useNavigate();
  const {
    isOpen: isVersionModalOpen,
    handleOpen: handleNewVersion,
    handleClose: handleVersionModalClose,
  } = useOpenClose();

  const { data, isLoading, isError } = useGetSeasonalPricingPlanQuery({ planGroupId }, { skip: !planGroupId });

  const handleBack = useCallback(() => navigate(seasonalPlansPath(programType)), [navigate, programType]);

  const historyColumns = useMemo<Column[]>(
    () => [
      { heading: 'Version', dataKey: 'version', width: 100 },
      {
        heading: 'Published',
        dataKey: 'meta',
        width: 180,
        render: (_cell?: unknown, row?: Row) => {
          const plan = row as SeasonalPricingPlan;
          return plan.meta?.createdAt ? <DateTimeDisplay value={plan.meta.createdAt} /> : '—';
        },
      },
      {
        heading: 'Annual Equivalent',
        dataKey: 'annualEquivalent',
        width: 170,
        render: (_cell?: unknown, row?: Row) => {
          const plan = row as SeasonalPricingPlan;
          return (
            <Badge
              variant={plan.annualEquivalent ? 'success' : 'warning'}
              message={plan.annualEquivalent ? 'Yes' : 'No'}
            />
          );
        },
      },
      {
        heading: 'Acknowledged By',
        dataKey: 'exception',
        render: (_cell?: unknown, row?: Row) => getAcknowledgedBy(row as SeasonalPricingPlan) ?? '—',
      },
    ],
    [],
  );

  if (isLoading) return <p className="pt-16">Loading…</p>;
  if (isError || !data) return <p className="pt-16">Unable to load this seasonal plan. Please try again.</p>;

  const { current, history } = data;
  const annualTotal = sumMultipliers(current.monthlyMultipliers);
  const acknowledgedBy = getAcknowledgedBy(current);

  return (
    <div className="flex flex-col gap-24 pt-16">
      <div className="flex flex-wrap items-center gap-16">
        <Button as="button" variant="secondary" tone="neutral" size="md" onClick={handleBack}>
          Back to Plans
        </Button>
        <h2 className="text-lg font-semibold">{current.name}</h2>
        <Badge variant="info" message={`Version ${current.version}`} />
        <Badge
          variant={current.annualEquivalent ? 'success' : 'warning'}
          message={current.annualEquivalent ? 'Annual-equivalent' : 'Not annual-equivalent'}
        />
      </div>

      <dl className="grid grid-cols-1 gap-16 sm:grid-cols-3">
        <div>
          <dt className="text-sm opacity-70">Annual Total</dt>
          <dd className="font-medium">
            {annualTotal}% of {ANNUAL_EQUIVALENT_TOTAL}%
          </dd>
        </div>
        <div>
          <dt className="text-sm opacity-70">Admin Fee</dt>
          <dd className="font-medium">
            {current.adminFeeMonthly === undefined ? '—' : `${currencyFormatter.format(current.adminFeeMonthly)}/mo`}
          </dd>
        </div>
        <div>
          <dt className="text-sm opacity-70">Acknowledged By</dt>
          <dd className="font-medium">{acknowledgedBy ?? '—'}</dd>
        </div>
      </dl>

      <div className="flex flex-col gap-12">
        <h3 className="font-medium">Monthly Multipliers</h3>
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-4">
          {MONTH_KEYS.map((monthKey) => (
            <div
              key={monthKey}
              className="rounded flex items-baseline justify-between gap-8 border border-[var(--color-border-separator)] p-12"
            >
              <span className="text-sm opacity-70">{MONTH_LABELS[monthKey]}</span>
              <span className="font-medium">
                {current.monthlyMultipliers[monthKey] === 100 ? '-' : `${current.monthlyMultipliers[monthKey]}%`}
              </span>
            </div>
          ))}
        </div>
      </div>

      <RequirePricingAdminUser>
        <div className="flex">
          <Button
            as="button"
            variant="primary"
            size="md"
            onClick={handleNewVersion}
            isDisabled={current.status === PricingPlanStatus.archived}
          >
            Publish New Version
          </Button>
        </div>
        <SeasonalPlanModal
          isModalOpen={isVersionModalOpen}
          closeModal={handleVersionModalClose}
          programType={programType}
          planGroupId={planGroupId}
          defaultValues={toFormValues(current)}
        />
      </RequirePricingAdminUser>

      {history.length > 0 && (
        <div className="flex flex-col gap-12">
          <h3 className="font-medium">Version History</h3>
          <Table rowKey="id" columns={historyColumns} rows={history} isScrollable={{ x: true }} />
        </div>
      )}
    </div>
  );
}
